/**
 * Subject List Page
 * View and manage subjects for a study
 * Columns adapt to user role; filters are inline per-column
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { subjectsApi, dataApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  Users,
  Plus,
  ChevronRight,
  UserPlus,
  X,
  AlertCircle,
  RotateCcw,
} from 'lucide-react';

// Small filter select used in column headers
const ColFilter = ({ value, onChange, options, placeholder }: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder: string;
}) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="w-full mt-1 px-1 py-0.5 text-[11px] font-normal normal-case tracking-normal border border-gray-200 rounded bg-white text-gray-600 focus:ring-1 focus:ring-opci-navy-300 focus:border-transparent"
  >
    <option value="">{placeholder}</option>
    {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

// Qualitative status pill: "Complete", "In Progress (3/15)", "Not Started"
const StatusPill = ({ complete, total, inProgress }: { complete: number; total: number; inProgress?: number }) => {
  if (total === 0) return <span className="text-xs text-gray-400">—</span>;
  const allDone = complete === total;
  const started = complete > 0 || (inProgress || 0) > 0;
  if (allDone) {
    return <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">Complete</span>;
  }
  if (started) {
    return <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700">In Progress ({complete}/{total})</span>;
  }
  return <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">Not Started</span>;
};

// Simple count badge for optional form types (Core Lab, CEC, Event-Driven)
const CountBadge = ({ count, color = 'gray' }: { count: number; color?: string }) => {
  if (count === 0) return <span className="text-xs text-gray-400">0</span>;
  const colors: Record<string, string> = {
    purple: 'bg-purple-50 text-purple-700',
    indigo: 'bg-indigo-50 text-indigo-700',
    orange: 'bg-orange-50 text-orange-700',
    red: 'bg-red-50 text-red-700',
    gray: 'bg-gray-100 text-gray-700',
  };
  return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${colors[color] || colors.gray}`}>{count}</span>;
};

const SubjectList: React.FC = () => {
  const { user, studyCode } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [addError, setAddError] = useState<string | null>(null);

  // Column filters
  const [fSubject, setFSubject] = useState(searchParams.get('siteId') || '');
  const [fSite, setFSite] = useState('');
  const [fDataEntry, setFDataEntry] = useState('');
  const [fImages, setFImages] = useState('');
  const [fEventDriven, setFEventDriven] = useState('');
  const [fCoreLab, setFCoreLab] = useState('');
  const [fCec, setFCec] = useState('');
  const [fQueries, setFQueries] = useState('');

  const hasFilters = fSubject || fSite || fDataEntry || fImages || fEventDriven || fCoreLab || fCec || fQueries;
  const clearFilters = () => { setFSubject(''); setFSite(''); setFDataEntry(''); setFImages(''); setFEventDriven(''); setFCoreLab(''); setFCec(''); setFQueries(''); };

  useEffect(() => {
    if (searchParams.get('siteId')) setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  const queryClient = useQueryClient();
  const selectedStudy = studyCode || '';
  const userAssignedSites = user?.assignedSites || [];
  const isSiteRC = user?.role === 'site-rc' || user?.role === 'site';
  const isAdmin = user?.role === 'admin';
  const isCec = user?.role === 'cec';
  const isAnalyst = user?.role === 'analyst';

  // Data queries
  const { data: subjectsData, isLoading } = useQuery({
    queryKey: ['subjects', selectedStudy],
    queryFn: () => subjectsApi.list(selectedStudy),
    enabled: !!selectedStudy,
  });

  const { data: sitesData } = useQuery({
    queryKey: ['sites'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/sites', { headers: { Authorization: `Bearer ${token}` } });
      return res.json();
    },
    enabled: isAdmin,
  });

  const { data: summaryData } = useQuery({
    queryKey: ['subject-summary', selectedStudy],
    queryFn: () => dataApi.subjectSummary(),
    enabled: !!selectedStudy,
  });

  const allSites = sitesData?.sites || [];
  const subjects = subjectsData?.subjects || [];
  const ss = summaryData?.subjects || {};

  // Unique sites for filter
  const uniqueSites = useMemo(() => {
    const acc: { _id: string; siteId: string; siteName: string }[] = [];
    for (const subj of subjects) {
      if (typeof subj.site === 'object' && subj.site) {
        const s = subj.site as { _id: string; siteId: string; siteName: string };
        if (!acc.some(a => a._id === s._id)) acc.push(s);
      }
    }
    return acc;
  }, [subjects]);

  // Helper to classify a summary section
  const classifyStatus = (section?: { total: number; complete: number; inProgress: number }) => {
    if (!section || section.total === 0) return 'none';
    if (section.complete === section.total) return 'complete';
    if (section.complete > 0 || section.inProgress > 0) return 'in-progress';
    return 'not-started';
  };

  // Filtered subjects
  const filteredSubjects = useMemo(() => subjects.filter(subject => {
    const summary = ss[subject._id];

    // Text search on subject ID
    if (fSubject) {
      const term = fSubject.toLowerCase();
      const display = (subject.displayId || subject.subjectId).toLowerCase();
      if (!display.includes(term)) return false;
    }
    // Site
    if (fSite) {
      const siteId = typeof subject.site === 'object' ? (subject.site as any)?._id : '';
      if (siteId !== fSite) return false;
    }
    // Data entry
    if (fDataEntry) {
      const s = classifyStatus(summary?.dataEntry);
      if (fDataEntry !== s) return false;
    }
    // Images
    if (fImages === 'yes' && (!summary || summary.images.count === 0)) return false;
    if (fImages === 'no' && summary && summary.images.count > 0) return false;
    // Event-Driven
    if (fEventDriven === 'yes' && (!summary || summary.eventDriven?.count === 0)) return false;
    if (fEventDriven === 'no' && summary && (summary.eventDriven?.count || 0) > 0) return false;
    // Core Lab
    if (fCoreLab === 'yes' && (!summary || summary.coreLab?.count === 0)) return false;
    if (fCoreLab === 'no' && summary && (summary.coreLab?.count || 0) > 0) return false;
    // CEC
    if (fCec === 'yes' && (!summary || summary.cec?.count === 0)) return false;
    if (fCec === 'no' && summary && (summary.cec?.count || 0) > 0) return false;
    // Queries (open count)
    if (fQueries === 'yes' && (!summary || summary.queries.open === 0)) return false;
    if (fQueries === 'no' && summary && summary.queries.open > 0) return false;

    return true;
  }), [subjects, ss, fSubject, fSite, fDataEntry, fImages, fEventDriven, fCoreLab, fCec, fQueries]);

  // Add subject
  const addSubjectMutation = useMutation({
    mutationFn: (data: { studyCode: string; siteId: string }) => subjectsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subjects'] });
      setShowAddModal(false);
      if (!(isSiteRC && userAssignedSites.length === 1)) setSelectedSiteId('');
      setAddError(null);
    },
    onError: (error: Error) => setAddError(error.message),
  });

  const handleAddSubject = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStudy) return;
    let siteIdToUse = selectedSiteId;
    if (isSiteRC && userAssignedSites.length === 1) siteIdToUse = userAssignedSites[0].siteId;
    if (!siteIdToUse) return;
    addSubjectMutation.mutate({ studyCode: selectedStudy, siteId: siteIdToUse });
  };

  useEffect(() => {
    if (isSiteRC && userAssignedSites.length === 1 && !selectedSiteId) {
      setSelectedSiteId(userAssignedSites[0].siteId);
    }
  }, [isSiteRC, userAssignedSites, selectedSiteId]);

  const progressFilterOpts = [
    { value: 'not-started', label: 'Not Started' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'complete', label: 'Complete' },
  ];
  const countFilterOpts = [
    { value: 'yes', label: 'Has Forms' },
    { value: 'no', label: 'None' },
  ];

  // Columns visible to role
  const showDataEntry = isAdmin || isSiteRC || isCec;
  const showEventDriven = isAdmin || isSiteRC || isCec;
  const showCoreLab = isAdmin || isSiteRC || isAnalyst || isCec;
  const showCec = isAdmin || isCec;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Subjects</h1>
          <p className="text-gray-500">Manage study subjects and view their data</p>
        </div>
        <div className="flex items-center gap-3">
          {hasFilters && (
            <button onClick={clearFilters} className="flex items-center gap-1 px-3 py-2 text-sm text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg">
              <RotateCcw size={14} /> Clear Filters
            </button>
          )}
          {selectedStudy && (isAdmin || isSiteRC) && (
            <button onClick={() => setShowAddModal(true)} className="flex items-center gap-2 px-4 py-2 bg-opci-navy-500 text-white rounded-lg hover:bg-opci-navy-600 transition-colors">
              <Plus size={20} /> Add Subject
            </button>
          )}
        </div>
      </div>

      {/* Subject Table */}
      <div className="bg-white rounded-xl shadow-sm">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-opci-navy-500"></div>
          </div>
        ) : subjects.length === 0 ? (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-300" />
            <p className="mt-2 text-gray-500">No subjects in this study yet</p>
            {(isAdmin || isSiteRC) && (
              <button onClick={() => setShowAddModal(true)} className="mt-4 inline-flex items-center gap-2 text-opci-navy-500 hover:text-opci-navy-600">
                <UserPlus size={18} /> Add your first subject
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                {/* Column headers */}
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Subject ID</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Site</th>
                  {showDataEntry && <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Data Entry</th>}
                  <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Images</th>
                  {showEventDriven && <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Events</th>}
                  {showCoreLab && <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Core Lab</th>}
                  {showCec && <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">CEC</th>}
                  <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Queries</th>
                  <th className="px-3 py-2 w-24"></th>
                </tr>
                {/* Inline filters row */}
                <tr className="bg-gray-50/50">
                  <th className="px-3 py-1">
                    <input
                      type="text"
                      value={fSubject}
                      onChange={(e) => setFSubject(e.target.value)}
                      placeholder="Search..."
                      className="w-full px-1.5 py-0.5 text-[11px] font-normal border border-gray-200 rounded bg-white text-gray-600 focus:ring-1 focus:ring-opci-navy-300 focus:border-transparent"
                    />
                  </th>
                  <th className="px-3 py-1">
                    <ColFilter value={fSite} onChange={setFSite} placeholder="All"
                      options={uniqueSites.map(s => ({ value: s._id, label: `${s.siteId} - ${s.siteName}` }))}
                    />
                  </th>
                  {showDataEntry && (
                    <th className="px-3 py-1">
                      <ColFilter value={fDataEntry} onChange={setFDataEntry} placeholder="All" options={progressFilterOpts} />
                    </th>
                  )}
                  <th className="px-3 py-1">
                    <ColFilter value={fImages} onChange={setFImages} placeholder="All"
                      options={[{ value: 'yes', label: 'Has Files' }, { value: 'no', label: 'No Files' }]}
                    />
                  </th>
                  {showEventDriven && (
                    <th className="px-3 py-1">
                      <ColFilter value={fEventDriven} onChange={setFEventDriven} placeholder="All" options={countFilterOpts} />
                    </th>
                  )}
                  {showCoreLab && (
                    <th className="px-3 py-1">
                      <ColFilter value={fCoreLab} onChange={setFCoreLab} placeholder="All" options={countFilterOpts} />
                    </th>
                  )}
                  {showCec && (
                    <th className="px-3 py-1">
                      <ColFilter value={fCec} onChange={setFCec} placeholder="All" options={countFilterOpts} />
                    </th>
                  )}
                  <th className="px-3 py-1">
                    <ColFilter value={fQueries} onChange={setFQueries} placeholder="All"
                      options={countFilterOpts}
                    />
                  </th>
                  <th className="px-3 py-1"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredSubjects.length === 0 ? (
                  <tr>
                    <td colSpan={99} className="px-4 py-8 text-center text-sm text-gray-500">
                      No subjects match the current filters
                    </td>
                  </tr>
                ) : filteredSubjects.map((subject) => {
                  const summary = ss[subject._id];
                  const qOpen = summary?.queries?.open || 0;
                  return (
                    <tr key={subject._id} className="hover:bg-gray-50">
                      <td className="px-3 py-3">
                        <span className="font-medium text-gray-900">{subject.displayId || subject.subjectId}</span>
                      </td>
                      <td className="px-3 py-3 text-gray-600 text-sm">
                        {typeof subject.site === 'object' ? subject.site?.siteName || subject.site?.siteId : subject.siteId}
                      </td>
                      {showDataEntry && (
                        <td className="px-3 py-3 text-center">
                          {summary ? <StatusPill complete={summary.dataEntry.complete} total={summary.dataEntry.total} inProgress={summary.dataEntry.inProgress} /> : <span className="text-xs text-gray-400">—</span>}
                        </td>
                      )}
                      <td className="px-3 py-3 text-center">
                        {summary && summary.images.count > 0 ? (
                          <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                            {summary.images.count} files
                          </span>
                        ) : <span className="text-xs text-gray-400">—</span>}
                      </td>
                      {showEventDriven && (
                        <td className="px-3 py-3 text-center">
                          <CountBadge count={summary?.eventDriven?.count || 0} color="orange" />
                        </td>
                      )}
                      {showCoreLab && (
                        <td className="px-3 py-3 text-center">
                          <CountBadge count={summary?.coreLab?.count || 0} color="purple" />
                        </td>
                      )}
                      {showCec && (
                        <td className="px-3 py-3 text-center">
                          <CountBadge count={summary?.cec?.count || 0} color="indigo" />
                        </td>
                      )}
                      <td className="px-3 py-3 text-center">
                        <CountBadge count={qOpen} color={qOpen > 0 ? 'red' : 'gray'} />
                      </td>
                      <td className="px-3 py-3 text-right">
                        <Link to={`/data?subject=${subject._id}`} className="inline-flex items-center gap-1 text-sm text-opci-navy-500 hover:text-opci-navy-600">
                          Data Entry <ChevronRight size={16} />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {filteredSubjects.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-100">
            <p className="text-sm text-gray-500">
              Showing {filteredSubjects.length} of {subjects.length} subjects
            </p>
          </div>
        )}
      </div>

      {/* Add Subject Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Add New Subject</h2>
              <button onClick={() => { setShowAddModal(false); setAddError(null); if (!(isSiteRC && userAssignedSites.length === 1)) setSelectedSiteId(''); }} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleAddSubject} className="p-6 space-y-4">
              {addError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <p className="text-sm text-red-700">{addError}</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Study</label>
                <input type="text" value={selectedStudy} disabled className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Site</label>
                {isSiteRC && userAssignedSites.length === 1 ? (
                  <div>
                    <input type="text" value={`${userAssignedSites[0].siteName} (${userAssignedSites[0].siteId})`} disabled className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50" />
                    <p className="text-xs text-green-600 mt-1">Your assigned site</p>
                  </div>
                ) : isSiteRC && userAssignedSites.length > 1 ? (
                  <div>
                    <select value={selectedSiteId} onChange={(e) => setSelectedSiteId(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg" required>
                      <option value="">-- Select your site --</option>
                      {userAssignedSites.map((site) => <option key={site._id} value={site.siteId}>{site.siteName} ({site.siteId})</option>)}
                    </select>
                  </div>
                ) : isAdmin ? (
                  <div>
                    <select value={selectedSiteId} onChange={(e) => setSelectedSiteId(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg" required>
                      <option value="">-- Select site --</option>
                      {allSites.filter((s: any) => s.status !== 'closed').map((site: any) => <option key={site._id} value={site.siteId}>{site.siteName} ({site.siteId})</option>)}
                    </select>
                  </div>
                ) : (
                  <p className="text-sm text-red-600">You don't have permission to add subjects</p>
                )}
              </div>
              {(selectedSiteId || (isSiteRC && userAssignedSites.length === 1)) && (
                <div className="bg-opci-navy-50 border border-opci-navy-100 rounded-lg p-3">
                  <p className="text-sm text-opci-navy-700">
                    <span className="font-medium">Subject ID Preview:</span>{' '}
                    <span className="font-mono">{isSiteRC && userAssignedSites.length === 1 ? userAssignedSites[0].siteId : selectedSiteId}-XXX</span>
                  </p>
                  <p className="text-xs text-opci-navy-500 mt-1">The sequence number (XXX) will be auto-assigned</p>
                </div>
              )}
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => { setShowAddModal(false); setAddError(null); setSelectedSiteId(''); }} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={addSubjectMutation.isPending || (!selectedSiteId && !(isSiteRC && userAssignedSites.length === 1))} className="px-4 py-2 bg-opci-navy-500 text-white rounded-lg hover:bg-opci-navy-600 disabled:opacity-50">
                  {addSubjectMutation.isPending ? 'Adding...' : 'Add Subject'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubjectList;
